import { motion } from 'framer-motion';
import { Mic, BookOpen, ArrowRight, Sparkles } from 'lucide-react';

interface ActionCardProps {
  title: string;
  subtitle: string;
  iconName: 'mic' | 'book' | 'sparkles';
  variant: 'primary' | 'secondary';
  size?: 'default' | 'hero';
  ctaLabel?: string;
  onPress: () => void;
}

const iconMap = {
  mic: Mic,
  book: BookOpen,
  sparkles: Sparkles,
} as const;

export default function ActionCard({
  title,
  subtitle,
  iconName,
  variant,
  size = 'default',
  ctaLabel,
  onPress,
}: ActionCardProps) {
  const Icon = iconMap[iconName];
  const isHero = size === 'hero';

  if (variant === 'primary') {
    return (
      <motion.button
        whileTap={{ scale: 0.98 }}
        whileHover={{ y: -2 }}
        onClick={onPress}
        className={`group relative w-full bg-gradient-to-br from-primary to-primary-dark rounded-2xl text-left cursor-pointer overflow-hidden ${
          isHero ? 'p-8 md:p-10 min-h-[260px] flex flex-col justify-between' : 'p-5'
        }`}
      >
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute -bottom-16 -left-8 w-40 h-40 rounded-full bg-white/[0.04] pointer-events-none" />

        <div className="relative">
          <Icon size={isHero ? 36 : 28} className="text-white mb-3" />
          <h3
            className={`font-display font-semibold text-white ${
              isHero ? 'text-3xl md:text-4xl mb-2' : 'text-lg'
            }`}
          >
            {title}
          </h3>
          <p
            className={`font-sans text-white/85 mt-1 ${
              isHero ? 'text-base md:text-lg max-w-md' : 'text-sm'
            }`}
          >
            {subtitle}
          </p>
        </div>

        {isHero && (
          <div className="relative mt-6 inline-flex items-center gap-2 self-start font-sans font-medium text-white/95">
            {ctaLabel ?? 'Begin practice'}
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </div>
        )}
      </motion.button>
    );
  }

  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      whileHover={{ y: -2 }}
      onClick={onPress}
      className="group w-full bg-card border-2 border-border rounded-2xl p-5 md:p-6 text-left cursor-pointer transition-colors hover:border-primary/30"
    >
      <Icon size={28} className="text-text-primary mb-3" />
      <h3 className="font-display font-semibold text-lg md:text-xl text-text-primary">
        {title}
      </h3>
      <p className="font-sans text-sm text-text-secondary mt-1">{subtitle}</p>
    </motion.button>
  );
}
